import * as animejs from 'animejs';
import { firePixelTracking } from 'shared/utils/fireTracking';
import { dispatchGlobalEvent, insertScriptIntoDom } from 'shared/utils/dom';
class AdaptScript {
    constructor() {
        this.timePointRecords = [];
        this.startFns = [];
        this.loopStartFns = [];
        this.completeFns = [];
        this.lastHandledTime = 0;
        this.onEventRecords = [];
        // expose to user
        this.anime = animejs;
        this.firePixelTracking = firePixelTracking;
        this.listenToEvents = () => {
            document.addEventListener('timelineStart', this.timelineStartHandler.bind(this));
            document.addEventListener('timelineLoopStart', this.timelineLoopStartHandler.bind(this));
            document.addEventListener('timelineProgress', this.timelineProgressHandler.bind(this));
            document.addEventListener('timelineCompleted', this.timelineCompleteHandler.bind(this));
        };
        this.removeEvents = () => {
            document.removeEventListener('timelineStart', this.timelineStartHandler.bind(this));
            document.removeEventListener('timelineLoopStart', this.timelineLoopStartHandler.bind(this));
            document.removeEventListener('timelineProgress', this.timelineProgressHandler.bind(this));
            document.removeEventListener('timelineCompleted', this.timelineCompleteHandler.bind(this));
        };
        this.listenToEvents();
    }
    reset() {
        this.timePointRecords = [];
        this.removeEvents();
        this.startFns = [];
        this.loopStartFns = [];
        this.completeFns = [];
        this.lastHandledTime = 0;
        return this;
    }
    /**
     * Consume and process script information from the given ad config.
     *
     * @param ad Full ad configuration
     * @param layoutId The current Layout. Passed to plugin initiators, etc.
     * @param pluginNames Names of plugins that have already been loaded via the `pluginLoader`.
     */
    processData(ad, layoutId, plugins = {}) {
        this.currentLayoutId = layoutId;
        this.attachPluginInstances(plugins);
        // load user script after all plugins attached
        this.addScript(ad.script);
        // record ad config info
        this.ad = ad;
        return this;
    }
    onStart(fn) {
        this.startFns.push(fn);
        return this;
    }
    onLoopStart(fn) {
        this.loopStartFns.push(fn);
        return this;
    }
    onCompleted(fn) {
        this.completeFns.push(fn);
        return this;
    }
    atTime(timePoint, fn) {
        const timeInMS = timePoint * 1000;
        this.recordTimePoint(timeInMS, fn);
        return this;
    }
    goTo(timePoint = 0) {
        const timeInMS = timePoint * 1000;
        dispatchGlobalEvent('timelineSeek', { time: timeInMS });
        return this;
    }
    stop() {
        dispatchGlobalEvent('timelineStop');
        return this;
    }
    play() {
        dispatchGlobalEvent('timelinePlay');
        return this;
    }
    getSymbolElement(symbolId) {
        return document.querySelector(`#ani-${symbolId}`);
    }
    onEvent(symbolId, eventName, fn) {
        const element = this.getSymbolElement(symbolId);
        if (!element) {
            // tslint:disable:no-console
            // eslint-disable-next-line
            console.error(`Symbol ${symbolId} not found.`);
            return;
        }
        // findIndex. screw you IE
        let recordIdx = -1;
        this.onEventRecords.forEach((record, idx) => {
            if (record.symbolId === symbolId &&
                record.eventName === eventName) {
                recordIdx = idx;
            }
        });
        // eventName for symbol already exists. Remove the previous
        if (recordIdx > -1) {
            element.removeEventListener(eventName, this.onEventRecords[recordIdx].fn);
            this.onEventRecords = [
                ...this.onEventRecords.slice(0, recordIdx),
                ...this.onEventRecords.slice(recordIdx + 1),
            ];
        }
        // reset pointer-events to prevents its interference with events
        element.style.pointerEvents = 'auto';
        const listener = (event) => {
            event.stopPropagation();
            fn(event, element);
        };
        this.onEventRecords.push({
            symbolId,
            eventName,
            fn: listener,
        });
        element.addEventListener(eventName, listener);
    }
    getCurrentLayoutId() {
        return this.currentLayoutId;
    }
    /**
     * access layout name via its id
     */
    getCurrentLayoutName() {
        const layout = this.ad.layouts[this.getCurrentLayoutId()];
        return layout ? layout.name : '';
    }
    /**
     * migration from v1 - fallback to the previous condition set method
     */
    getCurrentConditionSetId() {
        return this.getCurrentLayoutId();
    }
    getCurrentConditionSetName() {
        return this.getCurrentLayoutName();
    }
    /**
     * Attach all plugins to the AdaptScript instance,
     * so that they can be accessed by the User script.
     */
    attachPluginInstances(plugins) {
        Object.keys(plugins).forEach(name => {
            const instance = plugins[name];
            this[name] = instance;
        });
    }
    timelineProgressHandler(e) {
        const { time } = e.detail;
        if (this.timePointRecords.length === 0) {
            return;
        }
        if (this.lastHandledTime > time) {
            this.watchTimelineProgress(e.detail.time);
        }
        else {
            for (let i = this.lastHandledTime + 10; i <= time; i = i + 10) {
                this.watchTimelineProgress(i);
            }
        }
        this.lastHandledTime = time;
    }
    timelineStartHandler() {
        this.startFns.forEach(fn => fn());
    }
    timelineLoopStartHandler() {
        this.loopStartFns.forEach(fn => fn());
    }
    timelineCompleteHandler() {
        this.completeFns.forEach(fn => fn());
    }
    // monitor timeline change event dispatch
    // then executes the functions at those timePoints
    watchTimelineProgress(timePoint) {
        this.timePointRecords
            .filter(r => r.time === timePoint)
            .forEach(r => r.fn());
    }
    addScript(jsString) {
        insertScriptIntoDom(jsString, 'userScript');
    }
    recordTimePoint(time, fn) {
        this.timePointRecords.push({ time, fn });
    }
}
const singleton = new AdaptScript();
// exposes as global object for users
window.Adapt = singleton;
export default singleton;
